import React, { useState } from "react";

/**
 * Contact chip for a phone number on the order screens.
 *
 * The screens shipped with `handleContactCustomer = () => alert(...)`, which is
 * useless on the phone a dispatcher is actually holding — you cannot tap an
 * alert to dial. This renders the number as a real `tel:` link on touch devices
 * and a copy-to-clipboard control on desktop, where dialling does nothing.
 *
 * Props:
 *   label   "Vendor" | "Customer" — what this number belongs to
 *   name    optional display name shown above the number
 *   phone   the number; the component renders a muted placeholder when missing
 *   compact inline row (order summary) instead of a button
 */

// Touch-capable AND narrow — a touchscreen laptop should still get "copy".
const canDial = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(hover: none) and (pointer: coarse)")?.matches === true;

// Strip spaces/dashes/parens for the tel: target, keep a leading +.
export const dialable = (phone) => {
  const raw = String(phone || "").trim();
  if (!raw) return "";
  const plus = raw.startsWith("+") ? "+" : "";
  const digits = raw.replace(/[^\d]/g, "");
  return digits ? `${plus}${digits}` : "";
};

const ContactAction = ({ label = "Contact", name, phone, compact = false, style }) => {
  const [copied, setCopied] = useState(false);
  const tel = dialable(phone);

  if (!tel) {
    return (
      <span style={{ fontSize: 12.5, color: "#98a2b3", fontStyle: "italic", ...style }}>
        No {label.toLowerCase()} number on file
      </span>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(phone);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard is blocked on insecure origins — the number is already
      // on screen, so there is nothing to recover from.
    }
  };

  if (compact) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, ...style }}>
        <a href={`tel:${tel}`} style={{ fontWeight: 700, color: "#1d4ed8", textDecoration: "none" }} title={`Call ${name || label}`}>
          {phone}
        </a>
        {!canDial() && (
          <button type="button" onClick={copy}
            style={{ border: "1px solid #e4e7ec", background: "#fff", borderRadius: 6, fontSize: 11, fontWeight: 700, color: copied ? "#2e7d32" : "#667085", padding: "2px 8px", cursor: "pointer" }}>
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </span>
    );
  }

  return (
    <a
      href={`tel:${tel}`}
      onClick={(e) => { if (!canDial()) { e.preventDefault(); copy(); } }}
      title={canDial() ? `Call ${name || label} on ${phone}` : `Copy ${phone}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none",
        border: "1px solid #e4e7ec", background: "#fff", borderRadius: 8,
        padding: "8px 14px", fontSize: 13, fontWeight: 700, color: "#344054", ...style,
      }}
    >
      <span aria-hidden="true">📞</span>
      <span>
        {canDial() ? `Call ${label}` : copied ? "Number copied" : `Contact ${label}`}
        <span style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#667085" }}>
          {name ? `${name} · ${phone}` : phone}
        </span>
      </span>
    </a>
  );
};

export default ContactAction;
